import React, { useState } from 'react';
import { useTranslation } from '../../i18n';
import { PlayersList } from './PlayersList';
import { AppIcon } from './icons';
import '../../styles/lobby.css';

interface Player {
  id: string;
  name: string;
  score?: number;
  avatar?: string;
  isBot?: boolean;
}

interface PlayerListSectionProps {
  players: Player[];
  variant?: 'compact' | 'detailed' | 'grid';
  showScores?: boolean;
  title?: React.ReactNode;
  containerStyle?: React.CSSProperties;
  listStyle?: React.CSSProperties;
  className?: string;
  collapsible?: boolean;
  maxVisible?: number;
  renderPlayerActions?: (player: Player) => React.ReactNode;
  /** Placeholder rows while AI bots are connecting */
  pendingCount?: number;
  pendingLabel?: string;
  /** Override empty-state copy (e.g. AI promo when no players yet) */
  emptyMessage?: React.ReactNode;
  /** Fixed footer inside the panel (keeps column height stable) */
  footer?: React.ReactNode;
}

export const PlayerListSection: React.FC<PlayerListSectionProps> = ({
  players,
  variant = 'compact',
  showScores = false,
  title,
  containerStyle,
  listStyle,
  className,
  collapsible = false,
  maxVisible = 4,
  renderPlayerActions,
  pendingCount = 0,
  pendingLabel,
  emptyMessage,
  footer,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const defaultTitle = (
    <>
      <AppIcon name="people" fontSize="small" /> {t('common.players', { count: players.length })}
    </>
  );
  const displayTitle = title !== undefined ? (title === '' ? null : title) : defaultTitle;
  const hasMore = collapsible && players.length > maxVisible;
  const visiblePlayers = hasMore && !expanded ? players.slice(0, maxVisible) : players;
  const isDense = players.length > 8;
  const safePending = Math.max(0, pendingCount);
  const showWaiting = players.length === 0 && safePending === 0;

  return (
    <div
      className={`player-list-section ${collapsible ? 'player-list-section--compact' : ''} ${isDense ? 'player-list-section--dense' : ''} ${className || ''}`}
      style={containerStyle}
    >
      {displayTitle && (
        <h3 className="player-list-section-title">{displayTitle}</h3>
      )}
      {showWaiting ? (
        <div className="player-list-section__waiting-line">
          {emptyMessage || t('gameplay.partyHost.waitingForPlayers')}
        </div>
      ) : (
        <div className="player-list-section__body">
          <PlayersList
            players={visiblePlayers}
            variant={variant}
            showScores={showScores}
            className="players-container--lobby"
            style={listStyle}
            renderPlayerActions={renderPlayerActions}
          />
          {safePending > 0 && (
            <ul className="players-container players-container--lobby players-container--pending" aria-live="polite">
              {Array.from({ length: safePending }, (_, i) => (
                <li key={`pending-ai-${i}`} className="player-item player-item--pending">
                  <span className="player-item__pending-avatar" aria-hidden />
                  <span className="player-item__name player-item__name--pending">
                    {pendingLabel || t('lobby.ai.addingPlayer')}
                  </span>
                  <span className="player-item__pending-spinner" aria-hidden />
                </li>
              ))}
            </ul>
          )}
          {hasMore && (
            <button
              type="button"
              className="lobby-panel-expand-btn"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? t('common.showLess') : t('common.showAllPlayers', { count: players.length })}
            </button>
          )}
        </div>
      )}
      {footer}
    </div>
  );
};
